// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import { extent, max, min } from 'd3-array';
import Zion from './utils/Zion';
import { linearAxis, timeAxis } from '../../../factory/axes';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'Mar.',
    'Apr.',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;

const margins = {
  top: 20,
  right: 110,
  bottom: 25,
  left: 45,
};

const parser = d3.timeParse('%Y-%m-%d');

// let recessionData;
let minValue;
let maxValue;

let minDomain;
let maxDomain;

let lastData;
let lastUpdatedStr;
let latestMonth;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;
const pTime = d3.timeParse('%Y-%m-%d');

stylePremium(CNNPolitics, CNNBusiness);

const rParser = d3.timeParse('%Y-%m-%d');

let dekText;

d3.json('https://ix.cnn.io/data/gas-prices/aaa_gas_prices_avg_national_five_years.json').then((data) => {
  // Check for URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const freezeDateParam = urlParams.get('date');
  const yearsParam = urlParams.get('years');
  const yearParam = urlParams.get('year');
  let freezeDate = null;
  let yearsLimit = null;
  let specificYear = null;

  // Parse freeze date parameter (format: ?date=YYYYMMDD)
  if (freezeDateParam && freezeDateParam.length === 8) {
    const year = parseInt(freezeDateParam.substring(0, 4));
    const month = parseInt(freezeDateParam.substring(4, 6)) - 1; // Month is 0-indexed
    const day = parseInt(freezeDateParam.substring(6, 8));
    freezeDate = new Date(year, month, day);
    console.log('Freezing chart at date:', freezeDate);
  }

  // Parse years parameter (format: ?years=1 or ?years=2, etc.)
  if (yearsParam) {
    yearsLimit = parseInt(yearsParam);
    console.log('Limiting chart to', yearsLimit, 'year(s)');
  }

  // Parse year parameter (format: ?year=2026)
  if (yearParam) {
    specificYear = parseInt(yearParam);
    console.log('Showing calendar year:', specificYear);
  }

  // Map and filter data, removing any invalid entries
  chartData = data.data
    .map(d => ({
      date: d3.timeParse('%Y-%m-%d')(d.date),
      value: +d.price,
    }))
    .filter(d => d.date && !isNaN(d.value) && d.value !== null);

  // If freeze date is set, filter data to only include dates up to freeze date
  if (freezeDate) {
    chartData = chartData.filter(d => d.date <= freezeDate);
  }

  // If specific year is set, filter to that calendar year
  if (specificYear) {
    const yearStart = new Date(specificYear, 0, 1);
    const yearEnd = new Date(specificYear, 11, 31, 23, 59, 59);
    chartData = chartData.filter(d => d.date >= yearStart && d.date <= yearEnd);
  }

  // If years limit is set, filter data to only include the specified number of years
  if (yearsLimit && yearsLimit > 0) {
    // Find the maximum date in the (possibly frozen) data
    const maxDate = d3.max(chartData, d => d.date);
    // Calculate the minimum date by subtracting the specified years
    const minDate = new Date(maxDate);
    minDate.setFullYear(minDate.getFullYear() - yearsLimit);
    // Filter to only include data within the year range
    chartData = chartData.filter(d => d.date >= minDate);
  }

  // Validate we have data to display
  if (chartData.length === 0) {
    console.error('No valid gas price data available');
    d3.select('#graphic').append('p')
      .style('text-align', 'center')
      .style('padding', '40px')
      .text('Data temporarily unavailable');
    return;
  }

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max(chartData, d => d.date);

  minValue = d3.min(chartData, d => d.value) * 0.95;
  maxValue = d3.max(chartData, d => d.value) * 1.05;

  // Get the last data point
  const lastDataArray = chartData.filter(d => d.date.getTime() === maxDomain.getTime());
  lastData = lastDataArray.length > 0 ? lastDataArray[0] : chartData[chartData.length - 1];

  lastUpdatedStr = data.metadata.lastUpdatedStr;
  latestMonth = d3.timeFormat('%B %Y')(lastData.date);

  // Create editorial dek with highlighted values
  let latestPrice; let dayChange; let
    yearChange;

  if (freezeDate) {
    // Calculate values from filtered data
    latestPrice = lastData.value;

    // Find yesterday's price
    const yesterday = new Date(lastData.date);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayData = chartData.find(d => d.date.getTime() === yesterday.getTime());
    dayChange = yesterdayData ? lastData.value - yesterdayData.value : 0;

    // Find year ago price
    const yearAgo = new Date(lastData.date);
    yearAgo.setFullYear(yearAgo.getFullYear() - 1);
    const yearAgoData = chartData.find(d => d.date.getTime() === yearAgo.getTime());
    yearChange = yearAgoData ? lastData.value - yearAgoData.value : 0;

    // Update last updated string to reflect freeze date
    lastUpdatedStr = d3.timeFormat('%b. %-d, %Y at 9 a.m. ET')(lastData.date);
  } else {
    // Use metadata from API
    latestPrice = data.metadata.latestValue;
    dayChange = data.metadata.trends.dayChange;
    yearChange = data.metadata.trends.yearChange;
  }

  // Handle day change text
  let dayChangeText;
  if (dayChange === 0) {
    dayChangeText = 'unchanged from yesterday';
  } else {
    const dayDirection = dayChange > 0 ? 'up' : 'down';
    dayChangeText = `${dayDirection} <span class="highlight-value">$${Math.abs(dayChange).toFixed(2)}</span> from yesterday`;
  }

  // Handle year change text
  let yearChangeText;
  if (yearChange === 0) {
    yearChangeText = 'unchanged from this time a year ago';
  } else {
    const yearDirection = yearChange > 0 ? 'up' : 'down';
    yearChangeText = `${yearDirection} <span class="highlight-value">$${Math.abs(yearChange).toFixed(2)}</span> from this time a year ago`;
  }

  dekText = `The national average gas price is <span class="highlight-value">$${latestPrice.toFixed(2)}</span> per gallon, ${dayChangeText} and ${yearChangeText}.`;

  if (dekText && dekText !== '') {
    d3.select('h2.dek')
      .html(dekText);
  }

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xTicks = isMobile ? 2 : 5;
  const yTicks = isMobile ? 5 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  // Calculate time span in days to determine tick strategy
  const timeSpanDays = (maxDomain - minDomain) / (1000 * 60 * 60 * 24);
  const timeSpanYears = timeSpanDays / 365;

  let xAxis;

  // If showing ~1.5 years or less, use monthly ticks (every 2-3 months)
  if (timeSpanYears <= 1.5) {
    const allMonthTicks = [];
    const currentDate = new Date(minDomain.getFullYear(), minDomain.getMonth(), 1);

    while (currentDate <= maxDomain) {
      if (currentDate >= minDomain) {
        allMonthTicks.push(new Date(currentDate));
      }
      currentDate.setMonth(currentDate.getMonth() + 1);
    }

    // Adjust tick density based on time span
    let skipInterval;
    if (timeSpanDays <= 180) {
      // 6 months or less: show all months on desktop, every other on mobile
      skipInterval = isMobile ? 2 : 1;
    } else if (timeSpanDays <= 365) {
      // 1 year or less: show every 2 months on desktop, every 4 on mobile
      skipInterval = isMobile ? 6 : 2;
    } else {
      // More than 1 year: show every 3 months on desktop, every 4 on mobile
      skipInterval = isMobile ? 4 : 3;
    }

    const monthTicks = allMonthTicks.filter((d, i) =>
      // Always include first and last tick
      i === 0 || i === allMonthTicks.length - 1 || i % skipInterval === 0);

    xAxis = d3.axisBottom()
      .scale(xScale)
      .tickValues(monthTicks)
      .tickFormat((d) => {
        // Show month and year for January, or if it's the first/last tick
        if (d.getMonth() === 0 || d.getTime() === monthTicks[0].getTime() || d.getTime() === monthTicks[monthTicks.length - 1].getTime()) {
          return d3.timeFormat("%b '%y")(d);
        }
        // Show just month for other months
        return d3.timeFormat("%b '%y")(d);
      });
  } else {
    // For longer time spans, use yearly ticks
    const yearTicks = [];
    const startYear = minDomain.getFullYear();
    const endYear = maxDomain.getFullYear();

    for (let year = startYear; year <= endYear; year++) {
      // On mobile, only include even years
      if (isMobile && year % 2 !== 0) {
        continue;
      }

      const tickDate = new Date(year, 0, 1); // January 1st
      // Only include if it's within our data range (not at the very edges)
      if (tickDate > minDomain && tickDate < maxDomain) {
        yearTicks.push(tickDate);
      }
    }

    xAxis = d3.axisBottom()
      .scale(xScale)
      .tickValues(yearTicks)
      .tickFormat(d => d3.timeFormat('%Y')(d));
  }

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickFormat(d => `$${d3.format('.2f')(d)}`);

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  // Append zero line
  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(minValue))
    .attr('y2', yScale(minValue))
    .attr('stroke', '#393939')
    .attr('stroke-width', 1);

  const line = g.append('g')
    .attr('class', 'line');

  const lineWidth = window.innerWidth < 768 ? 1.5 : 2;

  line.append('path')
    .datum(chartData)
    .attr('stroke', d => '#f18851')
    .attr('stroke-width', lineWidth)
    .attr('fill', 'none')
    .attr('d', path);

  d3.select('.grid.y .domain').remove();

  // Add annotation for Russia invades Ukraine (Feb 24, 2022)
  const russiaUkraineDate = new Date(2022, 1, 24); // February 24, 2022

  // Only show annotation if date is within chart range
  if (russiaUkraineDate >= minDomain && russiaUkraineDate <= maxDomain) {
    const russiaAnnotation = g.append('g')
      .attr('class', 'russia-annotation');

    // Vertical dotted line
    russiaAnnotation.append('line')
      .attr('x1', xScale(russiaUkraineDate))
      .attr('x2', xScale(russiaUkraineDate))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4');

    // Date label (bold)
    const labelX = xScale(russiaUkraineDate) + 8;

    // Date halo
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .attr('class', 'russia-label-date-halo')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Feb. 24, 2022');

    // Date text
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .attr('class', 'russia-label-date')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text('Feb. 24, 2022');

    // First line halo
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('class', 'russia-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Russia invades');

    // First line text
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('class', 'russia-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Russia invades');

    // Second line halo
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 45)
      .attr('text-anchor', 'start')
      .attr('class', 'russia-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Ukraine');

    // Second line text
    russiaAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 45)
      .attr('text-anchor', 'start')
      .attr('class', 'russia-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('Ukraine');
  }

  // Add annotation for US/Iran war start date (Feb 28, 2026)
  const warStartDate = new Date(2026, 1, 28); // February 28, 2026

  // Only show annotation if date is within chart range
  if (warStartDate >= minDomain && warStartDate <= maxDomain) {
    const warAnnotation = g.append('g')
      .attr('class', 'war-annotation');

    // Vertical dotted line
    warAnnotation.append('line')
      .attr('x1', xScale(warStartDate))
      .attr('x2', xScale(warStartDate))
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#666')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,4');

    // Date label (bold)
    const labelX = xScale(warStartDate) + 8;

    // Date halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-date-halo')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('Feb. 28, 2026');

    // Date text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 15)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-date')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('fill', '#333')
      .text('Feb. 28, 2026');

    // First line halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('US and Israel');

    // First line text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('US and Israel');

    // Second line halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 45)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('initial strikes');

    // Second line text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 45)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('initial strikes');

    // Third line halo
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 60)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text('on Iran');

    // Third line text
    warAnnotation.append('text')
      .attr('x', labelX)
      .attr('y', 60)
      .attr('text-anchor', 'start')
      .attr('class', 'war-label-text')
      .style('font-size', '12px')
      .style('fill', '#666')
      .text('on Iran');
  }

  // Only add annotation if we have valid last data point
  if (lastData && lastData.date && !isNaN(lastData.value)) {
    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${[xScale(lastData.date), yScale(lastData.value)]})`);

    annotation.append('circle')
      .attr('r', '3')
      .attr('fill', '#f18851')
      .attr('stroke', 'none');

    // Date halo
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date-halo')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(d3.timeFormat('%b %-d, %Y')(lastData.date));

    // Date text
    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-date')
      .style('font-size', '12px')
      .style('font-weight', 'bold')
      .text(d3.timeFormat('%b %-d, %Y')(lastData.date));

    // Value halo
    annotation.append('text')
      .attr('dx', '10px')
      .attr('dy', '1.2em')
      .attr('class', 'annotation-value-halo')
      .style('font-size', '12px')
      .style('stroke', '#fff')
      .style('stroke-width', '3px')
      .style('fill', 'none')
      .text(`$${d3.format('.2f')(lastData.value)}`);

    // Value text
    annotation.append('text')
      .attr('dx', '10px')
      .attr('dy', '1.2em')
      .attr('class', 'annotation-value')
      .style('font-size', '12px')
      .text(`$${d3.format('.2f')(lastData.value)}`);
  }

  // Append the current date as a timestamp below the chart
  const currentDateString = `Notes: Shows price per gallon for regular gasoline. Last updated ${lastUpdatedStr}.`;

  const timestamp = container.append('div')
    .attr('class', 'footnote')
    .text(currentDateString);

  window.pymChild.sendHeight();
}
